import { CAPABILITY, DATA_SCOPE, getEffectiveScope } from "./permissions";
import { loadOrganizationMemberAuthUserIds } from "./membershipHelpers";
import {
  applyOrganizationScope,
  resolveOrganizationIdForQueries,
} from "./orgQueryScope";
import { resolveOrganizationIdFromActor } from "./organizationHelpers";
import {
  applyProjectVisibilityFilter,
  resolveScopedUserIds,
} from "./teamScope";
import { applyActiveTimelogFilter } from "./timeLogVisibility";

/** Sentinel user id — yields zero rows when scope resolves to an empty set. */
export const NO_SCOPE_MATCH_USER_ID =
  "00000000-0000-0000-0000-000000000000";

/**
 * Apply a resolved user-id list to a Supabase query on `supabase_user_id`.
 * `null` ids means organization scope (no filter).
 */
export function applyUserIdsToQuery(query, userIds, column = "supabase_user_id") {
  if (userIds === null) {
    return query;
  }

  if (!userIds || userIds.length === 0) {
    return query.eq(column, NO_SCOPE_MATCH_USER_ID);
  }

  if (userIds.length === 1) {
    return query.eq(column, userIds[0]);
  }

  return query.in(column, userIds);
}

/**
 * Apply Self / Team / Organization scope for a capability to a query.
 */
export function applySupabaseUserScope(
  query,
  actor,
  capability,
  context = {},
  column = "supabase_user_id",
) {
  const userIds = resolveScopedUserIds(actor, capability, context);
  return applyUserIdsToQuery(query, userIds, column);
}

/**
 * Resolve scoped user ids, restricting organization scope to org members when known.
 * RG-06: organization-wide scope without a positive organization_id fails closed
 * (empty list → deny sentinel), never unscoped.
 */
export async function resolveScopedUserIdsForQuery(
  supabaseClient,
  actor,
  capability,
  context = {},
) {
  const ids = resolveScopedUserIds(actor, capability, context);

  if (ids !== null) {
    return ids;
  }

  if (actor?.organization_id && supabaseClient) {
    return loadOrganizationMemberAuthUserIds(actor.organization_id, supabaseClient);
  }

  return [];
}

/**
 * Async user scope — applies org membership when capability scope is organization-wide.
 * Returns a query builder; do not `await` this helper (Supabase builders are thenable).
 */
export async function applySupabaseUserScopeForActor(
  query,
  supabaseClient,
  actor,
  capability,
  context = {},
  column = "supabase_user_id",
) {
  const userIds = await resolveScopedUserIdsForQuery(
    supabaseClient,
    actor,
    capability,
    context,
  );

  return applyUserIdsToQuery(query, userIds, column);
}

/**
 * Resolve payroll UI selection to user ids, including org membership for org-wide views.
 */
export async function resolvePayrollSelectionUserIdsForQuery(
  supabaseClient,
  actor,
  selection,
  context = {},
) {
  let userIds = resolvePayrollSelectionUserIds(actor, selection, context);

  if (userIds === null && actor?.organization_id && supabaseClient) {
    userIds = await loadOrganizationMemberAuthUserIds(
      actor.organization_id,
      supabaseClient,
    );
  }

  return userIds;
}

/**
 * Resolve report scope to user ids, including org membership for org-wide views.
 */
export async function resolveReportScopeUserIdsForQuery(
  supabaseClient,
  actor,
  reportScope,
  selectedEmployeeId,
  context = {},
) {
  let userIds = resolveReportScopeUserIds(
    actor,
    reportScope,
    selectedEmployeeId,
    context,
  );

  if (userIds === null && actor?.organization_id && supabaseClient) {
    userIds = await loadOrganizationMemberAuthUserIds(
      actor.organization_id,
      supabaseClient,
    );
  }

  return userIds;
}

/**
 * Resolve organization id for operational queries (sync when actor carries membership).
 */
export async function resolveOperationalOrganizationId(
  actor,
  supabaseClient,
  context = {},
) {
  return (
    resolveOrganizationIdFromActor(actor) ??
    (await resolveOrganizationIdForQueries(
      actor,
      supabaseClient,
      context.organizationId,
    ))
  );
}

/**
 * Apply organization_id to an operational time_logs query (Gate 5).
 */
export function applyOperationalTimeLogOrgScope(
  query,
  organizationId,
) {
  return applyOrganizationScope(query, organizationId, { tableName: "time_logs" });
}

/**
 * Apply organization_id to an active_sessions query (Gate 5).
 */
export function applyOperationalActiveSessionOrgScope(
  query,
  organizationId,
) {
  return applyOrganizationScope(query, organizationId, {
    tableName: "active_sessions",
  });
}

/**
 * Apply project visibility (team / assigned / org) to a projects query.
 */
export function applyProjectScopeFilter(query, actor, context = {}) {
  return applyProjectVisibilityFilter(
    query,
    actor,
    context.directReportIds ?? [],
  );
}

/**
 * Derive the visible client list from already-scoped projects.
 * The `clients` table has no ownership fields — scope flows from project visibility.
 */
export function deriveClientsFromProjects(projects = []) {
  const byId = new Map();

  for (const project of projects) {
    const clientId = project?.client_id;
    if (!clientId || byId.has(clientId)) {
      continue;
    }

    const nested = project.clients;
    const clientName =
      (nested && typeof nested === "object" && nested.client_name) ||
      project.client_name ||
      "Unknown Client";

    byId.set(clientId, {
      client_id: clientId,
      client_name: clientName,
    });
  }

  return Array.from(byId.values()).sort((a, b) =>
    String(a.client_name || "").localeCompare(
      String(b.client_name || ""),
      undefined,
      { sensitivity: "base" },
    ),
  );
}

/**
 * Whether `userId` is visible under the capability's effective data scope.
 */
export function isUserIdInCapabilityScope(
  actor,
  userId,
  capability,
  context = {},
) {
  if (!userId) {
    return true;
  }

  const ids = resolveScopedUserIds(actor, capability, context);

  if (ids === null) {
    return true;
  }

  return ids.includes(userId);
}

/**
 * Load users visible under a capability scope.
 */
export async function loadScopedUsers(
  supabaseClient,
  actor,
  capability,
  selectColumns,
  context = {},
) {
  if (!supabaseClient || !actor) {
    return [];
  }

  let query = supabaseClient
    .from("users")
    .select(selectColumns)
    .order("employee_name");

  const ids = await resolveScopedUserIdsForQuery(
    supabaseClient,
    actor,
    capability,
    context,
  );

  if (ids !== null) {
    if (ids.length === 0) {
      return [];
    }
    query = query.in("supabase_user_id", ids);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Load legacy users rows by auth id, restricted to organization members.
 */
export async function loadUsersByAuthIdsForOrg(
  supabaseClient,
  actor,
  userIds = [],
  selectColumns = "supabase_user_id, employee_name",
) {
  if (!supabaseClient || !actor || !userIds?.length) {
    return [];
  }

  const uniqueIds = [...new Set(userIds.filter(Boolean))];
  let scopedIds = uniqueIds;

  if (actor.organization_id) {
    const orgUserIds = await loadOrganizationMemberAuthUserIds(
      actor.organization_id,
      supabaseClient,
    );

    if (!orgUserIds.length) {
      return [];
    }

    scopedIds = uniqueIds.filter((id) => orgUserIds.includes(id));
  }

  if (!scopedIds.length) {
    return [];
  }

  const { data, error } = await supabaseClient
    .from("users")
    .select(selectColumns)
    .in("supabase_user_id", scopedIds);

  if (error) {
    throw error;
  }

  return data || [];
}

export const PAYROLL_SCOPE_SELECTION = Object.freeze({
  MY_TIME: "",
  MY_CREW: "MY_CREW",
  ALL_EMPLOYEES: "ALL_EMPLOYEES",
});

/**
 * Resolve payroll queue / report user filters from UI selection.
 */
export function resolvePayrollSelectionUserIds(
  actor,
  selection,
  context = {},
) {
  const actorId = actor?.supabase_user_id;
  const payrollScope = getEffectiveScope(
    actor,
    CAPABILITY.PAYROLL_QUEUE,
    context,
  );

  if (!actorId || !payrollScope) {
    return [];
  }

  if (payrollScope === DATA_SCOPE.SELF) {
    return [actorId];
  }

  const teamIds =
    resolveScopedUserIds(actor, CAPABILITY.PAYROLL_QUEUE, context) || [];

  if (selection === PAYROLL_SCOPE_SELECTION.MY_CREW) {
    return teamIds;
  }

  if (selection === PAYROLL_SCOPE_SELECTION.ALL_EMPLOYEES) {
    if (payrollScope === DATA_SCOPE.ORGANIZATION) {
      // RG-06: organization-wide selection requires positive membership org.
      return actor?.organization_id ? null : [];
    }
    return teamIds;
  }

  if (selection === PAYROLL_SCOPE_SELECTION.MY_TIME || !selection) {
    return [actorId];
  }

  if (isUserIdInCapabilityScope(actor, selection, CAPABILITY.PAYROLL_QUEUE, context)) {
    return [selection];
  }

  return [actorId];
}

export function applyPayrollSelectionUserScope(
  query,
  actor,
  selection,
  context = {},
) {
  const userIds = resolvePayrollSelectionUserIds(actor, selection, context);
  return applyUserIdsToQuery(query, userIds);
}

export async function applyPayrollSelectionUserScopeForActor(
  query,
  supabaseClient,
  actor,
  selection,
  context = {},
) {
  let userIds = resolvePayrollSelectionUserIds(actor, selection, context);

  if (userIds === null && actor?.organization_id && supabaseClient) {
    userIds = await loadOrganizationMemberAuthUserIds(
      actor.organization_id,
      supabaseClient,
    );
  }

  return applyUserIdsToQuery(query, userIds);
}

export const REPORT_SCOPE = Object.freeze({
  MY_TIME: "my_time",
  CREW_MEMBER: "crew_member",
  ENTIRE_TEAM: "entire_team",
  ENTIRE_ORGANIZATION: "entire_organization",
});

export function canUseReportScope(actor, reportScope, context = {}) {
  const timelogScope = getEffectiveScope(
    actor,
    CAPABILITY.TIMELOG_VIEW,
    context,
  );

  switch (reportScope) {
    case REPORT_SCOPE.MY_TIME:
      return true;
    case REPORT_SCOPE.CREW_MEMBER:
    case REPORT_SCOPE.ENTIRE_TEAM:
      return (
        timelogScope === DATA_SCOPE.TEAM ||
        timelogScope === DATA_SCOPE.ORGANIZATION
      );
    case REPORT_SCOPE.ENTIRE_ORGANIZATION:
      return timelogScope === DATA_SCOPE.ORGANIZATION;
    default:
      return false;
  }
}

export function resolveReportScopeUserIds(
  actor,
  reportScope,
  selectedEmployeeId,
  context = {},
) {
  const actorId = actor?.supabase_user_id;

  if (!actorId) {
    return [];
  }

  if (!canUseReportScope(actor, reportScope, context)) {
    return [actorId];
  }

  if (reportScope === REPORT_SCOPE.MY_TIME) {
    return [actorId];
  }

  if (reportScope === REPORT_SCOPE.CREW_MEMBER) {
    if (
      selectedEmployeeId &&
      isUserIdInCapabilityScope(
        actor,
        selectedEmployeeId,
        CAPABILITY.TIMELOG_VIEW,
        context,
      )
    ) {
      return [selectedEmployeeId];
    }
    return [actorId];
  }

  if (reportScope === REPORT_SCOPE.ENTIRE_TEAM) {
    return resolveScopedUserIds(actor, CAPABILITY.TIMELOG_VIEW, context);
  }

  if (reportScope === REPORT_SCOPE.ENTIRE_ORGANIZATION) {
    return actor?.organization_id ? null : [];
  }

  return [actorId];
}

export function applyReportScopeUserScope(
  query,
  actor,
  reportScope,
  selectedEmployeeId,
  context = {},
) {
  const userIds = resolveReportScopeUserIds(
    actor,
    reportScope,
    selectedEmployeeId,
    context,
  );
  return applyUserIdsToQuery(query, userIds);
}

export async function applyReportScopeUserScopeForActor(
  query,
  supabaseClient,
  actor,
  reportScope,
  selectedEmployeeId,
  context = {},
) {
  let userIds = resolveReportScopeUserIds(
    actor,
    reportScope,
    selectedEmployeeId,
    context,
  );

  if (userIds === null && actor?.organization_id && supabaseClient) {
    userIds = await loadOrganizationMemberAuthUserIds(
      actor.organization_id,
      supabaseClient,
    );
  }

  return applyUserIdsToQuery(query, userIds);
}

export const TIMELOG_HUB_SCOPE = Object.freeze({
  MY_TIME: "my_time",
  MY_TEAM: "my_team",
  ORGANIZATION: "organization",
});

export function canUseTimelogHubScope(actor, logScope, context = {}) {
  const timelogScope = getEffectiveScope(
    actor,
    CAPABILITY.TIMELOG_VIEW,
    context,
  );

  if (logScope === TIMELOG_HUB_SCOPE.MY_TIME) {
    return true;
  }

  if (logScope === TIMELOG_HUB_SCOPE.MY_TEAM) {
    return (
      timelogScope === DATA_SCOPE.TEAM ||
      timelogScope === DATA_SCOPE.ORGANIZATION
    );
  }

  if (logScope === TIMELOG_HUB_SCOPE.ORGANIZATION) {
    return timelogScope === DATA_SCOPE.ORGANIZATION;
  }

  return false;
}

/**
 * Apply organization_id filter when resolved (Gate 5).
 * Pass organizationId in context/options to skip async resolution.
 */
export async function applyOrganizationScopeForActor(
  query,
  supabaseClient,
  actor,
  tableName,
  context = {},
) {
  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    supabaseClient,
    context.organizationId,
  );

  return applyOrganizationScope(query, organizationId, { tableName });
}

/**
 * Apply organization scope to an operational time_logs query (Gate 5).
 */
export async function scopeOperationalTimeLogsForActor(
  query,
  supabaseClient,
  actor,
  context = {},
) {
  return applyOrganizationScopeForActor(
    query,
    supabaseClient,
    actor,
    "time_logs",
    context,
  );
}

/**
 * Apply organization scope to an active_sessions query (Gate 5).
 */
export async function scopeOperationalActiveSessionsForActor(
  query,
  supabaseClient,
  actor,
  context = {},
) {
  return applyOrganizationScopeForActor(
    query,
    supabaseClient,
    actor,
    "active_sessions",
    context,
  );
}

/**
 * Start a time_logs SELECT for operational workflows (excludes archived by default).
 */
export function selectOperationalTimeLogs(
  supabaseClient,
  select,
  { includeArchived = false, selectOptions } = {},
) {
  return applyActiveTimelogFilter(
    supabaseClient.from("time_logs").select(select, selectOptions),
    { includeArchived },
  );
}

/**
 * Apply manual time-entry hub filters (scope + optional employee pin).
 */
export function applyTimelogHubScope(
  query,
  actor,
  logScope,
  hubEmployeeFilter,
  context = {},
  options = {},
) {
  query = applyActiveTimelogFilter(query, options);
  if (hubEmployeeFilter) {
    if (
      !isUserIdInCapabilityScope(
        actor,
        hubEmployeeFilter,
        CAPABILITY.TIMELOG_VIEW,
        context,
      )
    ) {
      return query.eq("supabase_user_id", NO_SCOPE_MATCH_USER_ID);
    }

    return query.eq("supabase_user_id", hubEmployeeFilter);
  }

  if (!canUseTimelogHubScope(actor, logScope, context)) {
    return query.eq("supabase_user_id", actor?.supabase_user_id || NO_SCOPE_MATCH_USER_ID);
  }

  if (logScope === TIMELOG_HUB_SCOPE.MY_TIME) {
    return query.eq(
      "supabase_user_id",
      actor?.supabase_user_id || NO_SCOPE_MATCH_USER_ID,
    );
  }

  if (logScope === TIMELOG_HUB_SCOPE.ORGANIZATION) {
    // Org-wide user filter is intentional only with a positive membership org;
    // Gate 4 organization_id filter is applied separately via orgQueryScope.
    if (!actor?.organization_id) {
      return query.eq("supabase_user_id", NO_SCOPE_MATCH_USER_ID);
    }
    return query;
  }

  return applySupabaseUserScope(query, actor, CAPABILITY.TIMELOG_VIEW, context);
}

/**
 * Load unpaid payroll liability rows scoped to the actor (org / team / self).
 */
export async function loadScopedAccountsPayableTimelogs(
  supabaseClient,
  actor,
  context = {},
) {
  if (!supabaseClient || !actor) {
    return [];
  }

  let query = selectOperationalTimeLogs(
    supabaseClient,
    `
    supabase_user_id,
    employee,
    payroll_status,
    total_hours,
    hourly_rate
  `,
  );

  const userIds = await resolveScopedUserIdsForQuery(
    supabaseClient,
    actor,
    CAPABILITY.PAYROLL_QUEUE,
    context,
  );

  query = applyUserIdsToQuery(query, userIds);
  const organizationId = await resolveOperationalOrganizationId(
    actor,
    supabaseClient,
    context,
  );
  query = applyOperationalTimeLogOrgScope(query, organizationId);

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
}
